import { Combobox, Label, Field } from "@headlessui/react";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { SwitchButton } from "@/components/search";
import { Spinner } from "@/components/common/svgs";
import { getLocationString } from "@/utils/text";
import { API } from "@/services/API";
import { hasDigits } from "@/utils/query";
import { setCurrentSearch } from "@/state/currentSearchSlice";
import { useDispatch } from "react-redux";
import { useIsAnalyticRoute } from "@/routing";
import { getMarketFromQuery } from "@/utils/zips";
import { Check } from "lucide-react";

export const LocationDropdown = ({ name, urlQueryValues, value, setValue, query, setQuery, label, className, width, handleSwitch }) => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const errorText = "Error getting location list";
    const { zip, placeName, stateCode, withZip } = urlQueryValues;
    const [showZip, setShowZip] = useState(!!withZip);
    const dispatch = useDispatch();
    const isAnalyticRoute = useIsAnalyticRoute();
    const [isTyping, setIsTyping] = useState(Math.random());

    useEffect(() => {
        if (isAnalyticRoute) {
            clearStates(true);
        }
    }, [isAnalyticRoute]);

    const debouncedFetchData = useCallback(
        debounce((value) => {
            setLoading(true);
            if (value.length > 2) {
                API.get("/search/suggestions", {
                    params: {
                        name: value,
                    },
                })
                    .then((response) => {
                        setResults(response);
                    })
                    .catch((error) => {
                        setResults([]);
                        setError(errorText);
                        console.log(errorText, error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else if (value.length === 0) {
                API.get("/analytics/search-history", {
                    params: {
                        "number-of-responses": "10",
                    },
                })
                    .then((response) => {
                        const placeKV = {};
                        const places = [];
                        response.searches?.forEach((search) => {
                            placeKV[`${search.pickUp.placeName}-${search.pickUp.stateCode}`] = search.pickUp;
                            if (search.dropOff) {
                                placeKV[`${search.dropOff.placeName}-${search.dropOff.stateCode}`] = search.dropOff;
                            }
                        });
                        Object.keys(placeKV).forEach((key) => places.push(placeKV[key]));
                        setResults(places);
                    })
                    .catch((error) => {
                        setError(errorText);
                        console.log(errorText, "history", error);
                        setResults([]);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                setResults([]);
                setLoading(false);
            }
        }, 500),
        [],
    );

    const clearStates = () => {
        setQuery("");
        setValue(null);
        setResults([]);
        setLoading(false);
    };

    useEffect(() => {
        return () => {
            clearStates();
        };
    }, []);

    useEffect(() => {
        setShowZip(hasDigits(query));
        debouncedFetchData(query || "");
    }, [isTyping]);

    // When user types, we set different query value
    const handleInputChange = (event) => {
        setResults([]);
        setQuery(event.target.value);
        setIsTyping(Math.random());
    };

    // When width is 0 reset values
    useEffect(() => {
        if (!width) {
            clearStates();
        }
    }, [width]);

    useEffect(() => {
        const isPickup = name === "pickup";
        let valuesToSet = isPickup ? { pickup: null, pickupQuery: "" } : { delivery: null, deliveryQuery: "" };
        let newQuery = "";
        let newValue = null;
        if (zip && placeName && stateCode) {
            newValue = getMarketFromQuery(zip, placeName, stateCode);
            newQuery = getLocationString(newValue, withZip);
            valuesToSet = isPickup
                ? {
                      pickupQuery: newQuery,
                      pickup: newValue,
                  }
                : {
                      deliveryQuery: newQuery,
                      delivery: newValue,
                  };
        }
        setQuery(newQuery);
        dispatch(setCurrentSearch(valuesToSet));
        setValue(newValue);
    }, [zip]);

    useEffect(() => {
        let newQuery = "";
        let newValue = null;
        if (width && zip && placeName && stateCode) {
            newValue = getMarketFromQuery(zip, placeName, stateCode);
            newQuery = getLocationString(newValue, withZip);
        }
        setQuery(newQuery);
        setValue(newValue);
    }, [width]);

    return (
        <div className={`flex flex-row bg-cm-p-surface-container items-center z-20 relative ${className}`}>
            <Field
                className={`flex flex-col h-[64px] w-full md:min-w-[${width}px] md:max-w-[${width}px] px-4 py-[10px] relative transition-all ease-linear duration-50`}
            >
                <Label className="text-sm text-cm-p-text-secondary">{label}</Label>
                <Combobox
                    value={value}
                    onChange={(newValue) => {
                        setValue(newValue);
                        if (newValue) {
                            setQuery(getLocationString(newValue, showZip));
                            setResults([]);
                        }
                        setResults([]);
                    }}
                    immediate
                >
                    <Combobox.Input
                        className="w-full bg-cm-p-surface-container border-none !important outline-none focus:border-transparent focus:border-none focus:outline-none focus:ring-0 p-0 text-cm-p-text-primary placeholder:text-cm-text-tertiary font-medium placeholder-normal"
                        placeholder="City or ZIP"
                        onChange={handleInputChange}
                        displayValue={(location) => (Location ? getLocationString(location, showZip) : query || "")}
                        autoComplete="off"
                        data-open
                        onBlur={() => {
                            if (value && query !== getLocationString(value, showZip)) {
                                setValue(null);
                            }
                        }}
                        onFocus={handleInputChange}
                    />
                    {loading || (results.length === 0 && query && query.length > 0) || results.length ? (
                        <Combobox.Options
                            className={`absolute z-30 mt-[5px] max-h-[289px] top-[64px] left-0 w-full overflow-auto rounded-lg px-[1px] border border-cm-p-outline-variation drop-shadow-[0_4px_4px_rgba(0,0,0,0.08)] bg-cm-p-surface ${query !== "" || loading ? "py-[1px]" : ""}`}
                        >
                            {loading || (results.length === 0 && query && query.length > 0) ? (
                                <div className="relative cursor-default select-none py-2 px-3 text-cm-text-secondary min-h-[41px] flex items-center">
                                    {query.length > 0 && query.length < 3 ? (
                                        "Min 3 characters."
                                    ) : loading ? (
                                        <span className="inline-flex flex-row items-center">
                                            Loading <Spinner className="w-3 h-3 ml-4" />
                                        </span>
                                    ) : (
                                        "No results found."
                                    )}
                                </div>
                            ) : results.length ? (
                                results.map((result, idx) => (
                                    <Combobox.Option
                                        key={idx}
                                        value={result}
                                        className="relative w-full cursor-pointer select-none inline-flex min-h-[41px] px-3 items-center justify-between hover:bg-cm-p-surface-hover rounded-lg"
                                    >
                                        <>
                                            <span className="block truncate text-cm-text-primary font-medium text-sm">
                                                {`${getLocationString(result, showZip)}`}
                                            </span>
                                            {value?.zip === result.zip ? <Check className="size-4 text-cm-p-text-secondary" /> : null}
                                        </>
                                    </Combobox.Option>
                                ))
                            ) : null}
                        </Combobox.Options>
                    ) : null}
                </Combobox>
            </Field>
            {handleSwitch && <SwitchButton onClick={handleSwitch} />}
        </div>
    );
};
